import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import SearchIcon from '@mui/icons-material/Search';
import CustomCard from '../../Shared/Components/CustomCard/CustomCard';
import DataTable, { ColumnConfig } from '../../Shared/Components/DataTable';
import { useUnsettledBookingsAccountingReportPage } from '../Hooks/useUnsettledBookingsAccountingReportPage';
import { useGetReportsCompaniesLookupQuery } from '../Service/reportsCompaniesLookupApi';
import type {
  BookingSettlementReadinessFilter,
  UnsettledBookingAccountingReportRowDto,
} from '../Types/reports.types';

const fmt = (amount: number) =>
  amount.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const readinessLabel = (row: UnsettledBookingAccountingReportRowDto) =>
  row.readiness === 'ready' ? 'Gotowy' : 'Zablokowany';

const UnsettledBookingsAccountingReportPage: React.FC = () => {
  const {
    data,
    isLoading,
    isFetching,
    queryErrorMessage,
    validationError,
    draftFilters,
    filters,
    hasCompanyId,
    handleDraftFilterChange,
    handleSearchSubmit,
    handleResetFilters,
    handlePageChange,
    handlePageSizeChange,
    goToBookingSettlement,
  } = useUnsettledBookingsAccountingReportPage();

  const { companies } = useGetReportsCompaniesLookupQuery();

  const columns: ColumnConfig<UnsettledBookingAccountingReportRowDto>[] = React.useMemo(
    () => [
      { key: 'offerName', label: 'Oferta' },
      { key: 'bookingDateFrom', label: 'Od' },
      { key: 'bookingDateTo', label: 'Do' },
      { key: 'companyName', label: 'Firma' },
      { key: 'effectiveTravelMarginInvoiceCount', label: 'FM (szt.)' },
      {
        key: 'effectiveTravelMarginGrossTotal',
        label: 'FM brutto',
        render: (row) => fmt(row.effectiveTravelMarginGrossTotal),
      },
      { key: 'assignedPurchaseInvoiceCount', label: 'Zakupy (szt.)' },
      {
        key: 'assignedPurchaseAllocatedTotal',
        label: 'Zakupy przypisane',
        render: (row) => fmt(row.assignedPurchaseAllocatedTotal),
      },
      {
        key: 'readiness',
        label: 'Status',
        render: (row) => (
          <>
            <Chip
              size="small"
              label={readinessLabel(row)}
              color={row.readiness === 'ready' ? 'success' : 'warning'}
              data-testid={`readiness-chip-${row.bookingId}`}
            />
            {row.readiness === 'blocked' && row.blockingReasons.length > 0 && (
              <Typography variant="caption" display="block" color="text.secondary">
                {row.blockingReasons.join(', ')}
              </Typography>
            )}
          </>
        ),
      },
      {
        key: 'bookingId',
        label: 'Akcje',
        render: (row) => (
          <Button
            size="small"
            variant="outlined"
            onClick={() => goToBookingSettlement(row.bookingId)}
            data-testid={`goto-settlement-btn-${row.bookingId}`}
          >
            Przejdź do rozliczenia
          </Button>
        ),
      },
    ],
    [goToBookingSettlement]
  );

  return (
    <Stack spacing={2}>
      <CustomCard
        icon={<AssignmentLateIcon fontSize="large" color="primary" />}
        title="Nierozliczone bookingi"
        subheader="Przeglądaj i filtruj zakończone, nierozliczone bookingi"
        sx={{ mb: 4 }}
      >
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSearchSubmit();
          }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="company-select-label">Firma</InputLabel>
                <Select
                  labelId="company-select-label"
                  label="Firma"
                  value={draftFilters.companyId ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleDraftFilterChange({ companyId: value ? Number(value) : undefined });
                  }}
                  data-testid="company-select"
                >
                  <MenuItem value="">Wybierz firmę</MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={String(company.id)} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Zakończenie od"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={draftFilters.bookingDateToFrom ?? ''}
                onChange={(e) =>
                  handleDraftFilterChange({ bookingDateToFrom: e.target.value || undefined })
                }
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Zakończenie do"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={draftFilters.bookingDateToTo ?? ''}
                onChange={(e) =>
                  handleDraftFilterChange({ bookingDateToTo: e.target.value || undefined })
                }
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                fullWidth
                label="Status"
                size="small"
                value={draftFilters.readiness}
                onChange={(e) =>
                  handleDraftFilterChange({
                    readiness: e.target.value as BookingSettlementReadinessFilter,
                  })
                }
                data-testid="readiness-select"
              >
                <MenuItem value="all">Wszystkie</MenuItem>
                <MenuItem value="ready">Gotowe</MenuItem>
                <MenuItem value="blocked">Zablokowane</MenuItem>
              </TextField>
            </Grid>

            {validationError && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="error">{validationError}</Alert>
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  startIcon={<SearchIcon />}
                  data-testid="search-submit-btn"
                >
                  Wyszukaj
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  type="button"
                  startIcon={<FilterAltOffIcon />}
                  onClick={handleResetFilters}
                  data-testid="reset-filters-btn"
                >
                  Wyczyść filtry
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </CustomCard>

      {!hasCompanyId && (
        <Typography color="text.secondary" data-testid="no-company-state">
          Wybierz firmę, aby wyświetlić raport.
        </Typography>
      )}

      {hasCompanyId && queryErrorMessage && (
        <Alert severity="error" data-testid="error-state">
          {queryErrorMessage}
        </Alert>
      )}

      {hasCompanyId && (isLoading || isFetching) && !queryErrorMessage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }} data-testid="loading-state">
          <CircularProgress />
        </Box>
      )}

      {hasCompanyId && !isLoading && !queryErrorMessage && data?.items.length === 0 && (
        <Typography color="text.secondary" data-testid="empty-state">
          Brak zakończonych, nierozliczonych bookingów dla wybranych filtrów.
        </Typography>
      )}

      {hasCompanyId && !isLoading && !queryErrorMessage && data && data.items.length > 0 && (
        <Box data-testid="results-state">
          <CustomCard>
            <DataTable
              columns={columns}
              data={data.items}
              getRowId={(row) => row.bookingId}
              page={(filters.pageNumber ?? 1) - 1}
              rowsPerPage={filters.pageSize ?? 10}
              totalCount={data.totalCount}
              onPageChange={(newPage) => handlePageChange(newPage + 1)}
              onRowsPerPageChange={handlePageSizeChange}
            />
          </CustomCard>
        </Box>
      )}
    </Stack>
  );
};

export default UnsettledBookingsAccountingReportPage;
